import { formatRiskDecimal, formatRiskRoundTwoDecimals, getCancerRiskSuffix, normalizeCancerTypeName } from '@/utils';
import { Button, Divider, Flex, Modal, Text } from '@cmpsr/components';
import { Intervention } from '../../../types';
import ActionPlanBadge from '../../common/action-plan-badge';
import AddNewActionLink from '../../common/add-new-action-link';

type ModalCompleteActionItemProps = {
  intervention: Intervention | null;
  isOpen: boolean;
  onClose: () => void;
};

export default function ModalCompleteActionItem({ isOpen, intervention, onClose }: ModalCompleteActionItemProps) {
  if (!intervention) return null;
  const { riskImpact, overallRiskImpact, isNonQuantifiable } = intervention;
  const processedRiskImpact = riskImpact.filter(({ cancerType }) => cancerType !== 'overall');
  const sortedProcessedRiskImpact = processedRiskImpact.sort((a, b) => b.impact - a.impact);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <Modal.Overlay bg="background-overlay" backdropFilter="auto" />
      <Modal.Content
        bg="background-page"
        borderRadius="radii-2"
        p={{ base: 'spacer-5', lg: 'spacer-10' }}
        maxW={{ base: 'full', md: '654px' }}
        mx="spacer-4"
      >
        <Modal.Header
          display="flex"
          flexDirection="column"
          alignItems="center"
          w="full"
          gap={4}
          bg="background-page"
          p={0}
        >
          <Text variant="text-header-M" textAlign="center">
            {isNonQuantifiable ? (
              <>
                Congratulations! You have taken steps to{''}
                <Text as="span" textStyle="text-header-m-v" px="spacer-2">
                  reduce your cancer risk
                </Text>
              </>
            ) : (
              <>
                Congrats! Your estimated {riskImpact.length > 1 ? 'cancer risk' : `${riskImpact[0]?.cancerType} risk`}{' '}
                has
                <Text as="span" textStyle="text-header-m-v" px="spacer-2">
                  {Math.round(overallRiskImpact) === 0
                    ? 'decreased'
                    : `decreased by ${formatRiskRoundTwoDecimals(overallRiskImpact)}`}
                </Text>
              </>
            )}
          </Text>

          <Text variant="text-header-S" color="text-secondary" textAlign="center">
            Continue to follow your Action Plan to further reduce your risk
          </Text>
          <Divider />
        </Modal.Header>
        {riskImpact.length > 1 && (
          <Modal.Body
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            gap={2}
            my="spacer-4"
            border="1px solid"
            borderRadius="radii-3"
            borderColor="ui-element-divider"
          >
            {sortedProcessedRiskImpact.map(({ impact, cancerType }, index) => (
              <Flex alignItems="center" key={index} gap={4}>
                <ActionPlanBadge text={formatRiskDecimal(impact)} w="60px" />
                <Text variant="text-body-regular">
                  You reduced your {`${normalizeCancerTypeName(cancerType)}${getCancerRiskSuffix(cancerType)}`} risk
                </Text>
              </Flex>
            ))}
          </Modal.Body>
        )}
        <Modal.Footer display="flex" flexDirection="column" p={0} background="background-page">
          <Divider />
          <Flex flexDirection={{ base: 'column-reverse', lg: 'row' }} gap="spacer-4" pt="spacer-4" width="100%">
            <Button variant="primary-alt" size="m" onClick={onClose} w="full">
              Close
            </Button>
            <AddNewActionLink variant="fixed" />
          </Flex>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
